import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftOutlined, 
  SaveOutlined, 
  LoadingOutlined,
  EyeOutlined,
  CodeOutlined 
} from '@ant-design/icons';
import grapesjs from 'grapesjs';
import 'grapesjs/dist/css/grapes.min.css';
import gjsPresetWebpage from 'grapesjs-preset-webpage';
import gjsBlocksBasic from 'grapesjs-blocks-basic';
import gjsPluginForms from 'grapesjs-plugin-forms';
import gjsComponentCountdown from 'grapesjs-component-countdown';
import gjsPluginExport from 'grapesjs-plugin-export';
import gjsTabs from 'grapesjs-tabs';
import gjsCustomCode from 'grapesjs-custom-code';
import gjsTooltip from 'grapesjs-tooltip';
import gjsTyped from 'grapesjs-typed';
import gjsStyleGradient from 'grapesjs-style-gradient';
import { servicesService } from '../../services';
import { useNotificationContext } from '../../contexts/NotificationContext';

const TemplateEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { success, error: showError } = useNotificationContext();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [service, setService] = useState<any>(null);
  
  const editorRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadTemplate();
    
    return () => {
      if (editorRef.current) {
        editorRef.current.destroy();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadTemplate = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      
      // Cargar información del servicio
      const serviceData = await servicesService.getById(id);
      setService(serviceData);
      
      // Cargar el template HTML
      const html = await servicesService.getTemplate(id);
      
      // Inicializar GrapesJS
      initEditor(html);
      
    } catch (err: any) {
      console.error('Error loading template:', err);
      showError(err.message || 'Error al cargar el template');
      navigate(`/deploy-apps/${id}`);
    } finally {
      setLoading(false);
    }
  };

  const initEditor = (html: string) => {
    if (!containerRef.current) return;

    const editor = grapesjs.init({
      container: containerRef.current,
      height: '100%',
      width: '100%',
      plugins: [
        gjsPresetWebpage,
        gjsBlocksBasic,
        gjsPluginForms,
        gjsComponentCountdown,
        gjsPluginExport,
        gjsTabs,
        gjsCustomCode,
        gjsTooltip,
        gjsTyped,
        gjsStyleGradient,
      ],
      pluginsOpts: {
        [gjsPresetWebpage]: {
          modalImportTitle: 'Importar',
          modalImportLabel: '<div style="margin-bottom: 10px; font-size: 13px;">Pega aquí tu código HTML/CSS</div>',
          modalImportContent: (editor: any) => editor.getHtml() + '<style>' + editor.getCss() + '</style>',
          filestackOpts: null,
          aviaryOpts: false,
          blocksBasicOpts: {
            blocks: ['column1', 'column2', 'column3', 'column3-7', 'text', 'link', 'image', 'video', 'map'],
            flexGrid: 1,
          },
          blocks: ['link-block', 'quote', 'text-basic'],
          customStyleManager: [],
        },
        [gjsBlocksBasic]: {},
        [gjsPluginForms]: {},
        [gjsComponentCountdown]: {},
        [gjsPluginExport]: {},
        [gjsTabs]: {},
        [gjsCustomCode]: {},
        [gjsTooltip]: {},
        [gjsTyped]: {},
        [gjsStyleGradient]: {},
      },
      storageManager: false,
      canvas: {
        styles: [],
        scripts: [],
      },
      blockManager: {
        appendTo: '.blocks-container',
      },
      styleManager: {
        appendTo: '.styles-container',
        sectors: [
          {
            name: 'Dimensiones',
            open: false,
            buildProps: ['width', 'min-height', 'padding', 'margin'],
          },
          {
            name: 'Tipografía',
            open: false,
            buildProps: ['font-family', 'font-size', 'font-weight', 'letter-spacing', 'color', 'line-height', 'text-align'],
          },
          {
            name: 'Decoraciones',
            open: false,
            buildProps: ['background-color', 'border-radius', 'border', 'box-shadow', 'background'],
          },
          {
            name: 'Extra',
            open: false,
            buildProps: ['transition', 'perspective', 'transform'],
          },
        ],
      },
      layerManager: {
        appendTo: '.layers-container',
      },
      traitManager: {
        appendTo: '.traits-container',
      },
      selectorManager: {
        appendTo: '.styles-container',
      },
      panels: {
        defaults: [
          {
            id: 'layers',
            el: '.panel__right',
            resizable: {
              maxDim: 350,
              minDim: 200,
              tc: 0,
              cl: 1,
              cr: 0,
              bc: 0,
              keyWidth: 'flex-basis',
            },
          },
          {
            id: 'panel-switcher',
            el: '.panel__switcher',
            buttons: [
              {
                id: 'show-layers',
                active: true,
                label: 'Capas',
                command: 'show-layers',
                togglable: false,
              },
              {
                id: 'show-style',
                active: true,
                label: 'Estilos',
                command: 'show-styles',
                togglable: false,
              },
              {
                id: 'show-traits',
                active: true,
                label: 'Atributos',
                command: 'show-traits',
                togglable: false,
              },
            ],
          },
          {
            id: 'panel-devices',
            el: '.panel__devices',
            buttons: [
              {
                id: 'device-desktop',
                label: '💻',
                command: 'set-device-desktop',
                active: true,
                togglable: false,
              },
              {
                id: 'device-tablet',
                label: '📱',
                command: 'set-device-tablet',
                togglable: false,
              },
              {
                id: 'device-mobile',
                label: '📱',
                command: 'set-device-mobile',
                togglable: false,
              },
            ],
          },
        ],
      },
      deviceManager: {
        devices: [
          {
            name: 'Desktop',
            width: '',
          },
          {
            name: 'Tablet',
            width: '768px',
            widthMedia: '992px',
          },
          {
            name: 'Mobile',
            width: '320px',
            widthMedia: '480px',
          },
        ],
      },
    });

    // Comandos personalizados para los paneles
    editor.Commands.add('show-layers', {
      getRowEl(editor: any) { return editor.getContainer().closest('.editor-row'); },
      getLayersEl(row: any) { return row.querySelector('.layers-container'); },
      run(editor: any) {
        const lmEl = this.getLayersEl(this.getRowEl(editor));
        if (lmEl) lmEl.style.display = '';
      },
      stop(editor: any) {
        const lmEl = this.getLayersEl(this.getRowEl(editor));
        if (lmEl) lmEl.style.display = 'none';
      },
    });

    editor.Commands.add('show-styles', {
      getRowEl(editor: any) { return editor.getContainer().closest('.editor-row'); },
      getStyleEl(row: any) { return row.querySelector('.styles-container'); },
      run(editor: any) {
        const smEl = this.getStyleEl(this.getRowEl(editor));
        if (smEl) smEl.style.display = '';
      },
      stop(editor: any) {
        const smEl = this.getStyleEl(this.getRowEl(editor));
        if (smEl) smEl.style.display = 'none';
      },
    });

    editor.Commands.add('show-traits', {
      getRowEl(editor: any) { return editor.getContainer().closest('.editor-row'); },
      getTraitsEl(row: any) { return row.querySelector('.traits-container'); },
      run(editor: any) {
        const tmEl = this.getTraitsEl(this.getRowEl(editor));
        if (tmEl) tmEl.style.display = '';
      },
      stop(editor: any) {
        const tmEl = this.getTraitsEl(this.getRowEl(editor));
        if (tmEl) tmEl.style.display = 'none';
      },
    });

    editor.Commands.add('set-device-desktop', {
      run: (editor: any) => editor.setDevice('Desktop'),
    });
    editor.Commands.add('set-device-tablet', {
      run: (editor: any) => editor.setDevice('Tablet'),
    });
    editor.Commands.add('set-device-mobile', {
      run: (editor: any) => editor.setDevice('Mobile'),
    });

    // Parsear el HTML para extraer body y styles
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Extraer el contenido del body como string
    const bodyContent = doc.body ? doc.body.innerHTML : '';
    
    // Extraer los estilos
    const styleElements = doc.querySelectorAll('style');
    let cssContent = '';
    styleElements.forEach(style => {
      if (style.textContent) {
        cssContent += style.textContent + '\n';
      }
    });

    // Cargar el contenido en el editor
    if (bodyContent) {
      editor.setComponents(bodyContent);
    }
    
    // Agregar los estilos
    if (cssContent) {
      editor.setStyle(cssContent);
    }

    // Detectar cambios
    editor.on('component:add component:remove component:update style:update', () => {
      setHasChanges(true);
    });

    editorRef.current = editor;
  };

  const handleSave = async () => {
    if (!id || !hasChanges || !editorRef.current) return;
    
    try {
      setSaving(true);
      
      // Obtener el HTML completo del editor
      const html = editorRef.current.getHtml();
      const css = editorRef.current.getCss();
      
      // Combinar HTML y CSS
      const fullHtml = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    ${css}
  </style>
</head>
<body>
  ${html}
</body>
</html>
      `.trim();
      
      await servicesService.updateTemplate(id, fullHtml);
      success('Template guardado exitosamente');
      setHasChanges(false);
    } catch (err: any) {
      console.error('Error saving template:', err);
      showError(err.message || 'Error al guardar el template');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    if (service?.publicUrl) {
      window.open(service.publicUrl, '_blank');
    }
  };

  const handleViewCode = () => {
    if (!editorRef.current) return;
    
    const html = editorRef.current.getHtml();
    const css = editorRef.current.getCss();
    
    const fullCode = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
${css}
  </style>
</head>
<body>
${html}
</body>
</html>
    `.trim();
    
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(`
        <html>
          <head>
            <title>Código HTML - ${service?.name}</title>
            <style>
              body { margin: 0; padding: 20px; font-family: monospace; background: #1e1e1e; color: #d4d4d4; }
              pre { white-space: pre-wrap; word-wrap: break-word; }
            </style>
          </head>
          <body>
            <h2>Código HTML</h2>
            <pre>${fullCode.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
          </body>
        </html>
      `);
      newWindow.document.close();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <LoadingOutlined className="text-4xl text-blue-600 mb-4" spin />
          <p className="text-gray-600 dark:text-gray-400">Cargando editor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 z-10">
        <div className="flex items-center gap-4">
          <button
            onClick={() => {
              if (hasChanges) {
                if (window.confirm('Tienes cambios sin guardar. ¿Deseas salir sin guardar?')) {
                  navigate(`/deploy-apps/${id}`);
                }
              } else {
                navigate(`/deploy-apps/${id}`);
              }
            }}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeftOutlined />
            <span>Volver</span>
          </button>
          
          <div className="h-6 w-px bg-gray-300 dark:bg-gray-600" />
          
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
              Editor Visual
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {service?.name || 'Editando template'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="panel__devices"></div>
          
          <button
            onClick={handleViewCode}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
          >
            <CodeOutlined />
            <span className="hidden md:inline">Código</span>
          </button>

          {service?.publicUrl && (
            <button
              onClick={handlePreview}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <EyeOutlined />
              <span className="hidden md:inline">Vista Previa</span>
            </button>
          )}

          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <>
                <LoadingOutlined spin />
                <span className="hidden md:inline">Guardando...</span>
              </>
            ) : (
              <>
                <SaveOutlined />
                <span className="hidden md:inline">Guardar {hasChanges && '*'}</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Editor Layout */}
      <div className="flex-1 flex overflow-hidden editor-row">
        {/* Left Sidebar - Blocks */}
        <div className="w-64 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 overflow-y-auto">
          <div className="p-3">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Bloques</h3>
            <div className="blocks-container"></div>
          </div>
        </div>

        {/* Center - Canvas */}
        <div className="flex-1 overflow-hidden">
          <div ref={containerRef} className="h-full w-full" />
        </div>

        {/* Right Sidebar - Panels */}
        <div className="panel__right w-64 border-l border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 overflow-hidden flex flex-col">
          <div className="panel__switcher border-b border-gray-200 dark:border-gray-700 p-2 flex gap-1"></div>
          <div className="flex-1 overflow-y-auto p-3">
            <div className="layers-container mb-4"></div>
            <div className="styles-container mb-4"></div>
            <div className="traits-container mb-4"></div>
          </div>
        </div>
      </div>

      {/* Status Bar */}
      {hasChanges && (
        <div className="px-6 py-2 bg-yellow-50 dark:bg-yellow-900/20 border-t border-yellow-200 dark:border-yellow-800">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            Tienes cambios sin guardar
          </p>
        </div>
      )}
    </div>
  );
};

export default TemplateEditor;